// DatePicker.tsx
import React, { useState } from "react";
import dayjs from "dayjs";
import "tailwindcss/tailwind.css";
import useDatesStore from "../zustand/JoiningDetails";

interface DatePickerProps {
  onApply?: (dates: { MoveInDate: string; MoveOutDate: string; NoOfDays: number }) => void;
}

const DatePicker: React.FC<DatePickerProps> = ({ onApply }) => {
  const today = dayjs().format("YYYY-MM-DD");
  const [MoveInDate, setMoveInDate] = useState<string | null>(null);
  const [MoveOutDate, setMoveOutDate] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(dayjs().startOf("month"));

  const { setStartDate, setEndDate, setNoOfDays } = useDatesStore();

  const handleDateClick = (date: string) => {
    if (!MoveInDate || (MoveInDate && MoveOutDate)) {
      setMoveInDate(date);
      setMoveOutDate(null);
    } else if (dayjs(date).isAfter(MoveInDate)) {
      setMoveOutDate(date);
    } else {
      setMoveInDate(date);
      setMoveOutDate(null);
    }
  };

  const isDateInRange = (date: string) => {
    return MoveInDate && MoveOutDate && dayjs(date).isAfter(MoveInDate) && dayjs(date).isBefore(MoveOutDate);
  };

  const renderCalendar = (monthOffset: number) => {
    const firstDay = currentMonth.add(monthOffset, "month").startOf("month");
    const daysInMonth = firstDay.daysInMonth();
    const startDay = firstDay.day();
    const monthDays = Array.from({ length: startDay + daysInMonth }, (_, i) =>
      i >= startDay ? firstDay.date(i - startDay + 1).format("YYYY-MM-DD") : ""
    );

    return (
      <div className="p-2 w-1/2 min-w-[50px]">
        <h2 className="text-lg font-semibold text-center mb-1">
          {firstDay.format("MMMM YYYY")}
        </h2>
        <div className="grid grid-cols-7 text-center gap-1">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div key={d} className="font-medium p-1">
              {d}
            </div>
          ))}
          {monthDays.map((date, i) => (
            <button
              key={i}
              className={`p-1 m-1 rounded-full text-sm w-8 h-8 font-bold 
                ${date === MoveInDate || date === MoveOutDate ? "bg-[#E52067] text-white" : ""}
                ${isDateInRange(date) ? "bg-pink-300" : ""}
                ${date && dayjs(date).isBefore(today) ? "text-gray-300 cursor-not-allowed" : ""}`}
              onClick={() => date && !dayjs(date).isBefore(today) && handleDateClick(date)}
              disabled={!date || dayjs(date).isBefore(today)}
            >
              {date ? dayjs(date).date() : ""}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const handleApplyClick = () => {
    if (MoveInDate && MoveOutDate) {
      const NoOfDays = dayjs(MoveOutDate).diff(dayjs(MoveInDate), "day") + 1;
      // Update zustand store
      setStartDate(MoveInDate);
      setEndDate(MoveOutDate);
      setNoOfDays(NoOfDays);
      // Pass the values to the parent through the callback
      if (onApply) {
        onApply({ MoveInDate, MoveOutDate, NoOfDays });
      }
    }
  };

  return (
    <div className="max-w-2xl mx-auto bg-white shadow-lg rounded-lg p-4">
      <div className="flex justify-between items-center border-b p-2 relative">
        <div className="text-center w-1/2">
          <p className="text-md text-gray-500">Move-in</p>
          <p className={MoveInDate ? "text-md font-bold text-black" : "text-sm font-normal text-black"}>
            {MoveInDate ? dayjs(MoveInDate).format("MMM DD, YYYY") : "Select"}
          </p>
        </div>
        <div className="w-px h-10 bg-gray-300"></div>
        <div className="text-center w-1/2">
          <p className="text-md text-gray-500">Move-out</p>
          <p className={MoveOutDate ? "text-md font-bold text-black" : "text-sm font-normal text-black"}>
            {MoveOutDate ? dayjs(MoveOutDate).format("MMM DD, YYYY") : "Select"}
          </p>
        </div>
      </div>
      <div className="flex items-center justify-between p-2">
        <button onClick={() => setCurrentMonth(currentMonth.subtract(1, "month"))}>
          <svg className="w-6 h-6 text-gray-800" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 8 14">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 1 1.3 6.326a.91.91 0 0 0 0 1.348L7 13" />
          </svg>
        </button>
        <div className="flex gap-2">
          {renderCalendar(0)}
          {renderCalendar(1)}
        </div>
        <button onClick={() => setCurrentMonth(currentMonth.add(1, "month"))}>
          <svg className="w-6 h-6 text-gray-800" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 8 14">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 13 5.7-5.326a.909.909 0 0 0 0-1.348L1 1" />
          </svg>
        </button>
      </div>
      <hr />
      <div className="flex justify-between items-center p-2 w-full">
        <div className="flex-1">
          {MoveInDate && MoveOutDate && (
            <button className="flex items-center gap-2 p-2 bg-pink-100 text-pink-600 rounded-lg">
              📅 <span className="text-black">{dayjs(MoveOutDate).diff(dayjs(MoveInDate), "day") + 1} Days</span>
            </button>
          )}
        </div>
        <div className="flex gap-4">
          <button
            className="text-gray-500 underline"
            onClick={() => {
              setMoveInDate(null);
              setMoveOutDate(null);
            }}
          >
            Clear
          </button>
          <button className="bg-[#E52067] text-white px-3 py-1 w-24 rounded" onClick={handleApplyClick}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
};

export default DatePicker;
